// ============================================================================
// USE AVATAR MIND BUBBLES
// Periodically fires avatar "thought bubbles" to the second screen.
// Triggers: randomised timer + 6s after each phase transition.
// Timer interval and enabled state are read from workflow config on mount.
// Falls back to hardcoded defaults if config fetch fails.
// ============================================================================

import { useEffect, useRef, useCallback, useState } from 'react';
import { api } from '../services/api';
import type { SessionAvatar } from '../types/second-screen.types';

const DEFAULT_MIN_INTERVAL_MS = 3 * 60 * 1000;  // 3 minutes
const DEFAULT_MAX_INTERVAL_MS = 5 * 60 * 1000;  // 5 minutes
const PHASE_TRANSITION_DELAY_MS = 6_000;
const PHASE_TRANSITION_STAGGER_MS = 3_000;  // 3s between each avatar on phase transition

export function useAvatarMindBubbles(
  sessionId: string,
  sessionAvatars: SessionAvatar[],
  currentPhaseIndex: number,
  isActive: boolean,
  sessionEnabled: boolean,
  sendMindBubble: (role: string, name: string, thought: string) => void,
): void {
  const inProgressRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevPhaseIndexRef = useRef(currentPhaseIndex);

  // Config state — populated from backend on mount
  const [configEnabled, setConfigEnabled] = useState(true);
  const [minIntervalMs, setMinIntervalMs] = useState(DEFAULT_MIN_INTERVAL_MS);
  const [maxIntervalMs, setMaxIntervalMs] = useState(DEFAULT_MAX_INTERVAL_MS);
  const [phaseTransitionEnabled, setPhaseTransitionEnabled] = useState(true);

  // Fetch workflow config on mount
  useEffect(() => {
    api.get('/api/admin/workflow-config/mind-bubble')
      .then(({ data }) => {
        setConfigEnabled(data.enabled !== false);
        const timerTrigger = data.triggers?.find((t: any) => t.type === 'timer');
        const phaseTrigger = data.triggers?.find((t: any) => t.type === 'phase_transition');
        if (timerTrigger?.enabled === false) {
          // Timer disabled in config — set interval to 0 effectively disables scheduling
          setMinIntervalMs(0);
          setMaxIntervalMs(0);
        } else if (timerTrigger?.config?.intervalMinutes) {
          const ms = timerTrigger.config.intervalMinutes * 60_000;
          // Use the configured interval as both min and max for a fixed timer
          setMinIntervalMs(Math.round(ms * 0.85));
          setMaxIntervalMs(Math.round(ms * 1.15));
        }
        if (phaseTrigger !== undefined) {
          setPhaseTransitionEnabled(phaseTrigger.enabled !== false);
        }
      })
      .catch(() => {
        // Silently use hardcoded defaults if config fetch fails
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Fire a single bubble for one avatar (used by timer path — respects inProgressRef lock)
  const fireBubble = useCallback(async (avatar: SessionAvatar) => {
    if (inProgressRef.current) return;
    inProgressRef.current = true;
    try {
      const { data } = await api.post<{ thought: string; avatarRole: string; avatarName: string }>(
        `/api/sessions/${sessionId}/mind-bubble`,
        { avatarRole: avatar.role },
        { timeout: 42_000 },
      );
      if (data.thought) {
        sendMindBubble(avatar.role, avatar.name, data.thought);
      }
    } catch {
      // Silently fail — mind bubbles are ambient, not critical
    } finally {
      inProgressRef.current = false;
    }
  }, [sessionId, sendMindBubble]);

  // Fire a bubble without the inProgress lock (used by phase transition — concurrent calls intentional)
  // phaseIndex is passed explicitly so the backend always uses the correct phase name/objective.
  const fireBubbleUnlocked = useCallback(async (avatar: SessionAvatar, phaseIndex?: number) => {
    try {
      const { data } = await api.post<{ thought: string; avatarRole: string; avatarName: string }>(
        `/api/sessions/${sessionId}/mind-bubble`,
        { avatarRole: avatar.role, ...(phaseIndex != null && { phaseIndex }) },
        { timeout: 42_000 },
      );
      if (data.thought) {
        sendMindBubble(avatar.role, avatar.name, data.thought);
      }
    } catch {
      // Silently fail — mind bubbles are ambient, not critical
    }
  }, [sessionId, sendMindBubble]);

  const scheduleNext = useCallback(() => {
    clearTimer();
    if (!isActive || !configEnabled || !sessionEnabled || minIntervalMs === 0) return;
    const delay = minIntervalMs + Math.random() * (maxIntervalMs - minIntervalMs);
    timerRef.current = setTimeout(() => {
      if (!isActive || sessionAvatars.length === 0) {
        scheduleNext();
        return;
      }
      const avatar = sessionAvatars[Math.floor(Math.random() * sessionAvatars.length)];
      fireBubble(avatar).finally(() => scheduleNext());
    }, delay);
  }, [isActive, configEnabled, sessionEnabled, minIntervalMs, maxIntervalMs, sessionAvatars, fireBubble, clearTimer]);

  // Start/stop the recurring timer based on isActive and config
  useEffect(() => {
    if (isActive && configEnabled && sessionEnabled && sessionAvatars.length > 0) {
      scheduleNext();
    } else {
      clearTimer();
    }
    return clearTimer;
  }, [isActive, configEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fire on phase transition — all avatars, staggered 3s apart
  useEffect(() => {
    if (prevPhaseIndexRef.current === currentPhaseIndex) return;
    prevPhaseIndexRef.current = currentPhaseIndex;

    if (!isActive || !configEnabled || !sessionEnabled || !phaseTransitionEnabled || sessionAvatars.length === 0) return;

    const timers: ReturnType<typeof setTimeout>[] = [];
    sessionAvatars.forEach((avatar, i) => {
      const delay = PHASE_TRANSITION_DELAY_MS + i * PHASE_TRANSITION_STAGGER_MS;
      timers.push(setTimeout(() => fireBubbleUnlocked(avatar, currentPhaseIndex), delay));
    });

    return () => timers.forEach(t => clearTimeout(t));
  }, [currentPhaseIndex]); // eslint-disable-line react-hooks/exhaustive-deps
}
